import React from "react";
import { ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";

const projects = [
  {
    name: "STBL.io",
    tag: "Fintech · Asset-Backed Crypto",
    title: "Invest in Gold and Silver with the power of crypto.",
    copy:
      "Tokenized precious metals backed 1:1, blockchain-verified, and now spendable anywhere Visa is accepted. From the vault to your wallet.",
    metrics: [
      { label: "Market Projection", value: "$10T+" },
      { label: "Backing", value: "1:1" },
      { label: "Merchants", value: "100M+" },
    ],
    href: "https://stbl.io",
    image:
      "https://images.pexels.com/photos/11048045/pexels-photo-11048045.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-8 lg:row-span-2",
    accentLabel: "01 / Flagship",
  },
  {
    name: "2Connect.AI",
    tag: "Agentic AI · Networking",
    parent: "A Find Reciprocity venture",
    title: "Intelligent matchmaking on autopilot.",
    copy:
      "Agentic introductions for meaningful human relationships. The AI finds who you should meet — then makes the intro, with context.",
    metrics: [
      { label: "Frustrated Pros", value: "71%" },
      { label: "Warm Intro Lift", value: "8×" },
    ],
    href: "https://2connect.ai",
    image:
      "https://images.unsplash.com/photo-1740395816776-2687780e2fea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwY29uY2VwdCUyMGdvbGQlMjBibGFja3xlbnwwfHx8fDE3NzY5MTMzOTh8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-4",
    accentLabel: "02 / Operating",
  },
  {
    name: "GoldSilverCrypto.com",
    tag: "Brick & Mortar · Utah Valley",
    title: "Utah Valley's most trusted precious metals dealer.",
    copy:
      "Buy and sell gold, silver, platinum and palladium. Private appointments, spot-based pricing, same-day cash offers.",
    metrics: [
      { label: "Reviews", value: "5.0★" },
      { label: "Location", value: "Highland, UT" },
    ],
    href: "https://goldsilvercrypto.com",
    image:
      "https://images.pexels.com/photos/33539242/pexels-photo-33539242.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "lg:col-span-4",
    accentLabel: "03 / Local",
  },
];

const ProjectCard = ({ p, large }) => (
  <a
    href={p.href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`three-card-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    className={`card-luxe group relative flex flex-col justify-between p-8 lg:p-10 min-h-[340px] ${
      large ? "min-h-[540px] lg:min-h-[680px]" : ""
    }`}
  >
    {/* Image bg */}
    <div
      className="absolute inset-0 bg-cover bg-center opacity-[0.18] group-hover:opacity-30 transition-opacity duration-700"
      style={{ backgroundImage: `url(${p.image})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/70 to-black/30" />

    {/* content */}
    <div className="relative z-10 flex items-start justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">
        {p.accentLabel}
      </span>
      <ArrowUpRight className="w-5 h-5 text-gold/60 group-hover:text-gold group-hover:rotate-45 transition-all duration-500" />
    </div>

    <div className="relative z-10 mt-10">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">
        {p.tag}
      </div>
      <h3
        className={`heading-section text-white mb-4 ${
          large ? "text-5xl lg:text-7xl" : "text-3xl lg:text-4xl"
        }`}
      >
        <span className="block italic gold-gradient-text">{p.name}</span>
      </h3>
      {p.parent && (
        <div
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80 mb-4"
          data-testid={`three-card-parent-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
        >
          {p.parent}
        </div>
      )}
      <p
        className={`body-copy text-white/75 max-w-xl ${
          large ? "text-lg lg:text-xl" : "text-base"
        }`}
      >
        {p.title}
      </p>
      {large && (
        <p className="body-copy text-white/50 max-w-lg mt-4 text-sm">{p.copy}</p>
      )}

      <div className="flex flex-wrap gap-6 lg:gap-10 mt-8 pt-6 border-t border-gold-deep/60">
        {p.metrics.map((m) => (
          <div key={m.label}>
            <div
              className={`font-serif text-gold ${
                large ? "text-3xl lg:text-4xl" : "text-2xl"
              }`}
            >
              {m.value}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
              {m.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  </a>
);

export const TheThree = () => {
  return (
    <section
      id="the-three"
      data-testid="the-three-section"
      className="relative py-28 lg:py-40 z-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-24">
            <div>
              <div className="overline mb-6">
                <span className="ticker-dot mr-3" />
                The Three · Current Ventures
              </div>
              <h2 className="heading-section text-5xl sm:text-6xl lg:text-7xl text-white max-w-3xl">
                Three ventures.
                <br />
                One <span className="italic gold-gradient-text">through-line.</span>
              </h2>
              <p className="font-serif italic text-white/50 text-lg mt-4">
                — fondly, my three babies.
              </p>
            </div>
            <p className="body-copy max-w-md text-base lg:text-right">
              Three active companies across three different markets. One through-line —
              building things people can actually use, trust, and grow with.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-5 auto-rows-auto">
          {projects.map((p, idx) => (
            <Reveal key={p.name} delay={idx * 120} className={p.span}>
              <ProjectCard p={p} large={idx === 0} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TheThree;
